import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.js';

interface AdminStats {
  total_users: number;
  signups_today: number;
  active_today: number;
  active_this_week: number;
  active_this_month: number;
  total_clips: number;
  total_lessons_completed: number;
  most_watched_lesson: { title: string; view_count: number } | null;
}

export default function AdminStatsTab() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<AdminStats>('/api/admin/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-28 bg-surface-variant rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!stats) {
    return <p className="text-on-surface-variant">Could not load statistics.</p>;
  }

  const cards = [
    { label: 'Total users', value: stats.total_users },
    { label: 'Signups today', value: stats.signups_today },
    { label: 'Daily active', value: stats.active_today },
    { label: 'Weekly active', value: stats.active_this_week },
    { label: 'Monthly active', value: stats.active_this_month },
    { label: 'Clips uploaded', value: stats.total_clips },
    { label: 'Lessons completed', value: stats.total_lessons_completed },
    {
      label: 'Most watched lesson',
      value: stats.most_watched_lesson
        ? `${stats.most_watched_lesson.title} (${stats.most_watched_lesson.view_count} views)`
        : '—'
    }
  ];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2 bg-surface-variant border border-outline-variant rounded text-sm font-button hover:bg-surface-container-high transition-colors active:scale-[0.97]"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div
            key={card.label}
            className="bg-[#141414] thin-border rounded-lg p-6 flex flex-col justify-center min-h-[120px]"
          >
            <div className="text-[11px] uppercase tracking-widest text-on-surface-variant font-bold mb-2">
              {card.label}
            </div>
            <div className="text-2xl md:text-3xl font-bold text-on-surface break-words">{card.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
